import { isEmpty } from "lodash/fp";

const COLOR_PAIRS = [
  { bgcolor: "avatar.pink.main", color: "avatar.pink.dark" },
  { bgcolor: "avatar.red.main", color: "avatar.red.dark" },
  { bgcolor: "avatar.yellow.main", color: "avatar.yellow.dark" },
  { bgcolor: "avatar.redOrange.main", color: "avatar.redOrange.dark" },
  { bgcolor: "avatar.orange.main", color: "avatar.orange.dark" },
  { bgcolor: "avatar.yellowGreen.main", color: "avatar.yellowGreen.dark" },
  { bgcolor: "avatar.green.main", color: "avatar.green.dark" },
  { bgcolor: "avatar.teal.main", color: "avatar.teal.dark" },
  { bgcolor: "avatar.blue.main", color: "avatar.blue.dark" },
  { bgcolor: "avatar.indigo.main", color: "avatar.indigo.dark" },
  { bgcolor: "avatar.purple.main", color: "avatar.purple.dark" },
  { bgcolor: "avatar.magenta.main", color: "avatar.magenta.dark" },
];

export const hashFunc = <T>(input: string, colors: Array<T>) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + hash * 31;
    hash = hash & hash;
  }
  hash = ((hash % colors.length) + colors.length) % colors.length;
  return colors[hash] as T;
};

interface AvatarSx {
  bgcolor: string;
  color: string;
}

export const getAvatarSx = (
  input: string,
  colors: AvatarSx[] = COLOR_PAIRS
) => {
  if (input.length === 0) {
    return { bgcolor: "shades.neutral.200", color: "white" };
  }
  return hashFunc(input, colors);
};

export const waitForScrollEnd = (container: HTMLElement | null) => {
  let lastChangedFrame = 0;
  let lastScrollTop = container?.scrollTop ?? 0;

  return new Promise<void>((resolve) => {
    const tick = (frames: number) => {
      // requestAnimationFrame either for 500 frames or until 20 frames with
      // no change have been observed
      if (frames >= 500 || frames - lastChangedFrame > 20) {
        resolve();
      } else {
        if (container?.scrollTop != lastScrollTop) {
          lastChangedFrame = frames;
          lastScrollTop = container?.scrollTop ?? 0;
        }
        requestAnimationFrame(tick.bind(null, frames + 1));
      }
    };
    tick(0);
  });
};

export const getTotalWidth = (element: HTMLElement | null) => {
  if (!element) return 0;

  const { marginLeft, marginRight } = window.getComputedStyle(element);
  return element.offsetWidth + parseFloat(marginLeft) + parseFloat(marginRight);
};

export const getTotalHeight = (element: HTMLElement | null) => {
  if (!element) return 0;

  const { marginTop, marginBottom } = window.getComputedStyle(element);
  return (
    element.offsetHeight + parseFloat(marginTop) + parseFloat(marginBottom)
  );
};

/** Util for generating a random string to use as a unique key for mapped React components */
export const getUniqueKey = () =>
  `${Math.round(Math.random() * Math.random() * 100000000)}`;

const getVisibleItemRange = (virtuosoId: string) => {
  const virtuosoRef = document.getElementById(virtuosoId);
  if (!virtuosoRef) return { startIndex: -1, endIndex: -1 };

  const itemRefList = virtuosoRef.querySelectorAll("[data-item-index]");
  const itemRefs = Array.from(itemRefList);

  if (isEmpty(itemRefs)) return { startIndex: -1, endIndex: -1 };

  return {
    startIndex: parseInt(itemRefs[0].getAttribute("data-item-index") ?? "-1"),
    endIndex: parseInt(
      itemRefs[itemRefs.length - 1].getAttribute("data-item-index") ?? "-1"
    ),
  };
};

const ALLOWABLE_SMOOTH_SCROLL_DISTANCE = 2;

export const shouldSmoothScroll = (virtuosoId: string, targetIndex: number) => {
  const { startIndex, endIndex } = getVisibleItemRange(virtuosoId);
  return (
    targetIndex >= startIndex - ALLOWABLE_SMOOTH_SCROLL_DISTANCE &&
    targetIndex <= endIndex + ALLOWABLE_SMOOTH_SCROLL_DISTANCE
  );
};
